/**
 * SourcingPackageDetail — UEIM-02
 * Route: /crm/sourcing-packages/:packageId
 * Shows: Package info, invitations, comparison, selection
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { sourcingApi } from "@/services/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ChevronLeft, Package, Mail, BarChart3, CheckSquare,
  Copy, Send, RefreshCw, XCircle, CheckCircle2,
  ChevronRight, Clock, AlertCircle,
} from "lucide-react";

const PKG_STATUS_COLORS = {
  DRAFT:      "bg-slate-100 text-slate-600",
  OPEN:       "bg-blue-100 text-blue-700",
  CLOSED:     "bg-emerald-100 text-emerald-700",
  CANCELLED:  "bg-red-100 text-red-700",
};
const INV_STATUS_COLORS = {
  PENDING:           "bg-slate-100 text-slate-600",
  SENT:              "bg-blue-100 text-blue-700",
  OPENED:            "bg-indigo-100 text-indigo-700",
  RESPONSE_STARTED:  "bg-amber-100 text-amber-700",
  SUBMITTED:         "bg-emerald-100 text-emerald-700",
  REVISION_REQUESTED:"bg-orange-100 text-orange-700",
  EXPIRED:           "bg-red-100 text-red-600",
  REVOKED:           "bg-red-200 text-red-800",
};

const TABS = [
  { id: "overview",     label: "Overview",      icon: Package },
  { id: "invitations",  label: "Invitations",   icon: Mail },
  { id: "compare",      label: "Comparison",    icon: BarChart3 },
  { id: "selections",   label: "Selections",    icon: CheckSquare },
];

function CopyButton({ text, label = "Copy Link" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 transition-colors" data-testid="copy-invite-link">
      {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
}

export default function SourcingPackageDetail() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [pkg, setPkg] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [selections, setSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTab, setLoadingTab] = useState(false);
  const [revisionModal, setRevisionModal] = useState(null);
  const [revisionReason, setRevisionReason] = useState("");

  const canManage   = hasCapability(user, "sourcing.package.manage");
  const canSend     = hasCapability(user, "sourcing.invitation.send");
  const canCompare  = hasCapability(user, "sourcing.compare");
  const canSelect   = hasCapability(user, "sourcing.select");
  const canRevise   = hasCapability(user, "sourcing.response.request_revision");

  const loadPkg = useCallback(async () => {
    try {
      const res = await sourcingApi.getPackage(packageId);
      setPkg(res.data);
    } catch { toast.error("Package not found"); }
    finally { setLoading(false); }
  }, [packageId]);

  const loadTab = useCallback(async (tabId) => {
    setLoadingTab(true);
    try {
      if (tabId === "invitations") {
        const res = await sourcingApi.listInvitations(packageId);
        setInvitations(res.data || []);
      } else if (tabId === "compare") {
        const res = await sourcingApi.comparePackage(packageId);
        setComparison(res.data);
      } else if (tabId === "selections") {
        const res = await sourcingApi.listSelections(packageId);
        setSelections(res.data || []);
      }
    } catch { toast.error("Failed to load data"); }
    finally { setLoadingTab(false); }
  }, [packageId]);

  useEffect(() => { loadPkg(); }, [loadPkg]);
  useEffect(() => { if (pkg) loadTab(tab); }, [tab, pkg]);

  const handleRevoke = async (invId) => {
    try {
      await sourcingApi.revokeInvitation(packageId, invId, { reason: "Revoked by organiser" });
      toast.success("Invitation revoked");
      loadTab("invitations");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to revoke"); }
  };

  const handleResend = async (invId) => {
    try {
      await sourcingApi.resendInvitation(packageId, invId, { expiry_days: 7 });
      toast.success("Invitation resent with refreshed link");
      loadTab("invitations");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resend"); }
  };

  const handleRequestRevision = async () => {
    if (!revisionModal || !revisionReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await sourcingApi.requestRevision(packageId, {
        invitation_id: revisionModal.invitation_id,
        reason: revisionReason,
      });
      toast.success("Revision requested — supplier can now resubmit");
      setRevisionModal(null);
      setRevisionReason("");
      loadTab("compare");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to request revision"); }
  };

  const handleSelectLine = async (requirementItemId, supplierId, submissionId, lineItemId) => {
    try {
      await sourcingApi.selectLine(packageId, {
        requirement_item_id: requirementItemId,
        supplier_id: supplierId,
        submission_id: submissionId,
        line_item_id: lineItemId,
      });
      toast.success("Supplier selected for this item");
      loadTab("compare");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to select supplier"); }
  };

  const handleSelectBulk = async (supplierId, submissionId) => {
    try {
      const res = await sourcingApi.selectBulk(packageId, { supplier_id: supplierId, submission_id: submissionId });
      toast.success(`Selected supplier for ${res.data?.selected} item(s)`);
      loadTab("compare");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to bulk select"); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen text-slate-500">Loading…</div>;
  if (!pkg) return <div className="flex items-center justify-center min-h-screen text-slate-500">Package not found</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start gap-4 mb-8">
          <button onClick={() => navigate(-1)} className="mt-1 text-slate-400 hover:text-slate-700">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <Package className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl font-bold text-slate-900" data-testid="package-title">{pkg.title}</h1>
              <Badge className={`${PKG_STATUS_COLORS[pkg.status]} border-0 text-xs`} data-testid="package-status">
                {pkg.status}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 ml-0.5">
              {pkg.service_category_name} · Deadline: {pkg.response_deadline || "Not set"} · {pkg.items?.length || 0} requirement item(s)
            </p>
            {pkg.description && <p className="text-sm text-slate-600 mt-1 ml-0.5">{pkg.description}</p>}
          </div>
          <div className="flex gap-2">
            {canManage && pkg.status === "DRAFT" && (
              <Button size="sm" onClick={async () => {
                try { await sourcingApi.openPackage(packageId); toast.success("Package opened"); loadPkg(); }
                catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }} data-testid="open-package-btn">Open Package</Button>
            )}
            {canManage && pkg.status === "OPEN" && (
              <Button size="sm" variant="outline" onClick={async () => {
                try { await sourcingApi.closePackage(packageId); toast.success("Package closed"); loadPkg(); }
                catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }} data-testid="close-package-btn">Close Package</Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-slate-200 p-1 w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`pkg-tab-${id}`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW tab ── */}
        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Requirement Items in Package</h3>
              {pkg.items?.length === 0 ? (
                <p className="text-slate-400 text-sm">No items</p>
              ) : (
                <div className="space-y-3">
                  {pkg.items?.map((item, idx) => (
                    <div key={item.package_item_id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg" data-testid={`pkg-item-${item.requirement_item_id}`}>
                      <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800">{item.description}</p>
                        <p className="text-sm text-slate-500 mt-0.5">
                          Qty: {item.quantity} {item.unit}
                          {item.function_code && ` · Function: ${item.function_code}`}
                          {item.delivery_location && ` · ${item.delivery_location}`}
                        </p>
                        {(item.requested_delivery_start || item.requested_delivery_end) && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" />
                            {item.requested_delivery_start} {item.requested_delivery_end ? `→ ${item.requested_delivery_end}` : ""}
                          </p>
                        )}
                        <Badge className={`mt-1 text-xs border-0 ${item.status === "SOURCING_SELECTED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {item.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Delivery Context</h3>
              <div className="space-y-3 text-sm">
                {pkg.delivery_context?.delivery_location && (
                  <div><span className="text-slate-400">Location</span><p className="text-slate-700 font-medium">{pkg.delivery_context.delivery_location}</p></div>
                )}
                {pkg.delivery_context?.delivery_start && (
                  <div><span className="text-slate-400">Start</span><p className="text-slate-700 font-medium">{pkg.delivery_context.delivery_start}</p></div>
                )}
                {pkg.delivery_context?.delivery_end && (
                  <div><span className="text-slate-400">End</span><p className="text-slate-700 font-medium">{pkg.delivery_context.delivery_end}</p></div>
                )}
                {pkg.delivery_context?.function_code && (
                  <div><span className="text-slate-400">Function</span><p className="text-slate-700 font-medium">{pkg.delivery_context.function_code}</p></div>
                )}
                {pkg.response_deadline && (
                  <div><span className="text-slate-400">Response Deadline</span><p className="text-slate-700 font-medium">{pkg.response_deadline}</p></div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── INVITATIONS tab ── */}
        {tab === "invitations" && (
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Supplier Invitations</h3>
              <span className="text-sm text-slate-500">{invitations.length} sent</span>
            </div>
            {loadingTab ? (
              <div className="p-8 text-center text-slate-500">Loading…</div>
            ) : invitations.length === 0 ? (
              <div className="p-8 text-center" data-testid="invitations-empty">
                <Mail className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No invitations sent yet</p>
                <p className="text-slate-400 text-sm mt-1">Go to the Opportunity Requirements tab to create invitations</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {invitations.map(inv => (
                  <div key={inv.id} className="flex items-center gap-4 p-5" data-testid={`invitation-row-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800">{inv.supplier_name}</p>
                      <p className="text-sm text-slate-500">{inv.supplier_email}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge className={`${INV_STATUS_COLORS[inv.status]} border-0 text-xs`}>{inv.status}</Badge>
                        <span className="text-xs text-slate-400">Expires {new Date(inv.token_expires_at).toLocaleDateString()}</span>
                        {inv.token_fingerprint && <span className="text-xs text-slate-300 font-mono">…{inv.token_fingerprint}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {inv.quote_url && inv.status !== "REVOKED" && (
                        <CopyButton text={inv.quote_url} label="Copy Link" />
                      )}
                      {canSend && inv.status !== "REVOKED" && inv.status !== "SUBMITTED" && (
                        <Button size="sm" variant="outline" onClick={() => handleResend(inv.id)} data-testid={`resend-inv-${inv.id}`}>
                          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                        </Button>
                      )}
                      {canManage && inv.status !== "REVOKED" && (
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => handleRevoke(inv.id)} data-testid={`revoke-inv-${inv.id}`}>
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── COMPARISON tab ── */}
        {tab === "compare" && canCompare && (
          <div>
            {loadingTab ? (
              <div className="text-center py-8 text-slate-500">Loading comparison…</div>
            ) : (
              <>
                {/* Revision-requested banner */}
                {comparison?.revision_requested_count > 0 && (
                  <div
                    className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4"
                    data-testid="revision-requested-banner"
                  >
                    <span className="text-amber-500 mt-0.5">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Revision requested from {comparison.revision_requested_count} supplier{comparison.revision_requested_count !== 1 ? "s" : ""}
                        {comparison.revision_requested_supplier_names?.length > 0 && (
                          <span className="font-normal text-amber-700"> — {comparison.revision_requested_supplier_names.join(", ")}</span>
                        )}
                      </p>
                      <p className="text-xs text-amber-600 mt-0.5">Their previous responses are hidden until they resubmit. Once resubmitted, they will appear here.</p>
                    </div>
                  </div>
                )}
                {!comparison || comparison.comparison?.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border border-slate-200" data-testid="compare-empty">
                    <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No supplier responses yet</p>
                  </div>
                ) : (
              <div className="space-y-6" data-testid="comparison-table">
                {comparison.comparison?.map((row) => {
                  const suppliers = row.supplier_quotes || [];
                  const responded = suppliers.filter(s => s.response_available);
                  return (
                    <div key={row.requirement_item_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      <div className="bg-slate-50 px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{row.description}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Qty: {row.quantity} {row.unit} · {responded.length}/{suppliers.length} responses</p>
                        </div>
                        {row.selected_supplier_id && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Selected
                          </Badge>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                              <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-36">Supplier</th>
                              <th className="text-right px-4 py-2.5 font-medium text-slate-600">Unit Price</th>
                              <th className="text-right px-4 py-2.5 font-medium text-slate-600">Total</th>
                              <th className="text-center px-4 py-2.5 font-medium text-slate-600">GST</th>
                              <th className="text-left px-4 py-2.5 font-medium text-slate-600">Delivery By</th>
                              <th className="text-left px-4 py-2.5 font-medium text-slate-600">Payment Terms</th>
                              <th className="text-left px-4 py-2.5 font-medium text-slate-600">Notes</th>
                              {canSelect && <th className="px-4 py-2.5 font-medium text-slate-600">Action</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {suppliers.map(sq => {
                              const isSelected = row.selected_supplier_id === sq.supplier_id;
                              return (
                                <tr key={sq.supplier_id} className={`${isSelected ? "bg-emerald-50" : "hover:bg-slate-50"} transition-colors`} data-testid={`compare-row-${sq.supplier_id}`}>
                                  <td className="px-4 py-3">
                                    <p className="font-medium text-slate-800">{sq.supplier_name}</p>
                                    {isSelected && <span className="text-xs text-emerald-600 font-medium">Selected</span>}
                                    {sq.alternate_product_offered && <span className="text-xs text-amber-600 block">Alt. product offered</span>}
                                  </td>
                                  {sq.response_available ? (
                                    <>
                                      <td className="px-4 py-3 text-right font-medium text-slate-800">₹{sq.unit_price?.toLocaleString() ?? "—"}</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">₹{sq.total_amount?.toLocaleString() ?? "—"}</td>
                                      <td className="px-4 py-3 text-center text-xs text-slate-600">
                                        {sq.gst_applicable ? `${sq.gst_percent || 0}%` : "No"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-600">{sq.delivery_by || "—"}</td>
                                      <td className="px-4 py-3 text-slate-600 text-xs max-w-[140px] truncate">{sq.payment_terms || "—"}</td>
                                      <td className="px-4 py-3 text-slate-500 text-xs max-w-[140px] truncate">{sq.notes || "—"}</td>
                                    </>
                                  ) : (
                                    <td colSpan={6} className="px-4 py-3 text-center text-slate-400 text-xs italic">No response yet</td>
                                  )}
                                  {canSelect && (
                                    <td className="px-4 py-3 text-center">
                                      {sq.response_available && !isSelected && (
                                        <div className="flex flex-col gap-1 items-center">
                                          <Button
                                            size="sm"
                                            className="text-xs h-7"
                                            onClick={() => handleSelectLine(row.requirement_item_id, sq.supplier_id, sq.submission_id, sq.line_item_id)}
                                            data-testid={`select-line-${sq.supplier_id}`}
                                          >
                                            Select Item
                                          </Button>
                                          <button
                                            onClick={() => handleSelectBulk(sq.supplier_id, sq.submission_id)}
                                            className="text-xs text-indigo-600 hover:underline"
                                            data-testid={`select-all-${sq.supplier_id}`}
                                          >
                                            Select all items
                                          </button>
                                        </div>
                                      )}
                                      {isSelected && (
                                        <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" />
                                      )}
                                      {canRevise && sq.response_available && (
                                        <button
                                          onClick={() => setRevisionModal({ invitation_id: invitations.find(i => i.supplier_id === sq.supplier_id)?.id, supplier_name: sq.supplier_name })}
                                          className="text-xs text-amber-600 hover:underline mt-1 block"
                                          data-testid={`request-revision-${sq.supplier_id}`}
                                        >
                                          Request Revision
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
          </div>
        )}

        {/* ── SELECTIONS tab ── */}
        {tab === "selections" && (
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Confirmed Supplier Selections</h3>
              <p className="text-sm text-slate-500 mt-0.5">Each selection is an immutable record. Re-selection creates a new record and supersedes the previous.</p>
            </div>
            {loadingTab ? <div className="p-8 text-center text-slate-500">Loading…</div> :
              selections.length === 0 ? (
                <div className="p-8 text-center" data-testid="selections-empty">
                  <CheckSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No selections made yet</p>
                  <p className="text-slate-400 text-sm mt-1">Use the Comparison tab to select suppliers for each item</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {selections.map(sel => (
                    <div key={sel.id} className="flex items-center gap-4 p-5" data-testid={`selection-row-${sel.id}`}>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{sel.supplier_name}</p>
                        <p className="text-sm text-slate-600 mt-0.5">
                          {sel.requirement_description || "Requirement item"}
                          {sel.requirement_quantity ? ` — Qty: ${sel.requirement_quantity} ${sel.requirement_unit || ""}` : ""}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Rev {sel.revision_number} · Selected {new Date(sel.selected_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}
      </div>

      {/* Revision Request Modal */}
      {revisionModal && (
        <Dialog open={!!revisionModal} onOpenChange={() => setRevisionModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Quotation Revision</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Requesting a revision from <strong>{revisionModal.supplier_name}</strong>. They will be able to re-submit their quotation.
              </p>
              <div>
                <Label>Reason for Revision *</Label>
                <Textarea
                  value={revisionReason}
                  onChange={e => setRevisionReason(e.target.value)}
                  placeholder="Please provide updated pricing for the main stage and include delivery charges…"
                  className="mt-1"
                  data-testid="revision-reason"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setRevisionModal(null)}>Cancel</Button>
                <Button onClick={handleRequestRevision} data-testid="confirm-revision-btn">Request Revision</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
